import { defaultConfig } from "./config";
import type { CatalogEntry, LanguageDescriptor, LanguageOverride } from "./types";

export const PLAINTEXT_ID = "plaintext";

const lower = (s: string) => s.toLowerCase();

/** Last path segment: "src/app/Main.py" → "Main.py". */
export function basename(path: string): string {
  return path.slice(Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\")) + 1);
}

/** Defines a language override or a brand-new language. See README. */
export function defineLanguage(override: LanguageOverride): LanguageOverride {
  return override;
}

/**
 * Lookup over language descriptors: by id/alias, extension, filename, and shebang interpreter.
 * Built from the generated catalog plus local overrides (src/langs/*).
 */
export class LanguageRegistry {
  readonly #byId = new Map<string, LanguageDescriptor>();
  readonly #byAlias = new Map<string, LanguageDescriptor>();
  readonly #byExtension = new Map<string, LanguageDescriptor>();
  readonly #byFilename = new Map<string, LanguageDescriptor>();
  readonly #byFilenameLower = new Map<string, LanguageDescriptor>();
  readonly #byInterpreter = new Map<string, LanguageDescriptor>();
  #maxExtensionDots = 1;

  constructor(catalog: readonly CatalogEntry[] = [], overrides: readonly LanguageOverride[] = []) {
    for (const entry of catalog) this.register({ ...entry, config: defaultConfig(entry.id) });
    for (const override of overrides) this.register(override);
  }

  /** Adds a language, or merges into an existing one with the same id (later wins). */
  register(override: LanguageOverride): LanguageDescriptor {
    const existing = this.#byId.get(override.id);
    const merged: LanguageDescriptor = {
      id: override.id,
      name: override.name ?? existing?.name ?? override.id,
      aliases: [...new Set([...(existing?.aliases ?? []), ...(override.aliases ?? [])])],
      extensions: [...new Set([...(existing?.extensions ?? []), ...(override.extensions ?? [])])],
      filenames: [...new Set([...(existing?.filenames ?? []), ...(override.filenames ?? [])])],
      interpreters: [
        ...new Set([...(existing?.interpreters ?? []), ...(override.interpreters ?? [])]),
      ],
      config: { ...(existing?.config ?? defaultConfig(override.id)), ...override.config },
    };
    const grammar = override.grammar ?? existing?.grammar;
    if (grammar) merged.grammar = grammar;
    const runner = override.runner ?? existing?.runner;
    if (runner) merged.runner = runner;

    this.#byId.set(merged.id, merged);
    for (const alias of [merged.id, ...merged.aliases]) this.#byAlias.set(lower(alias), merged);
    for (const ext of merged.extensions) {
      this.#byExtension.set(lower(ext), merged);
      this.#maxExtensionDots = Math.max(this.#maxExtensionDots, ext.split(".").length - 1);
    }
    for (const name of merged.filenames) {
      this.#byFilename.set(name, merged);
      if (!this.#byFilenameLower.has(lower(name))) this.#byFilenameLower.set(lower(name), merged);
    }
    for (const interpreter of merged.interpreters) this.#byInterpreter.set(interpreter, merged);
    return merged;
  }

  /** By id or alias, case-insensitive. */
  get(idOrAlias: string): LanguageDescriptor | undefined {
    return this.#byId.get(idOrAlias) ?? this.#byAlias.get(lower(idOrAlias));
  }

  all(): LanguageDescriptor[] {
    return [...this.#byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  /** Exact filename match (case-sensitive first, then case-insensitive). */
  byFilename(path: string): LanguageDescriptor | undefined {
    const name = basename(path);
    return this.#byFilename.get(name) ?? this.#byFilenameLower.get(lower(name));
  }

  /** Longest matching extension, so "x.d.ts" and "x.blade.php" beat ".ts" and ".php". */
  byExtension(path: string): LanguageDescriptor | undefined {
    const name = lower(basename(path));
    const parts = name.split(".");
    // A leading dot ("".bashrc") is a hidden-file prefix, not an extension.
    const first = parts[0] === "" ? 2 : 1;
    const start = Math.max(first, parts.length - this.#maxExtensionDots);
    for (let i = start; i < parts.length; i++) {
      const match = this.#byExtension.get(`.${parts.slice(i).join(".")}`);
      if (match) return match;
    }
    return undefined;
  }

  byInterpreter(interpreter: string): LanguageDescriptor | undefined {
    return this.#byInterpreter.get(interpreter);
  }

  /** Filename, then extension. Content-based detection lives in the detection pipeline. */
  forPath(path: string): LanguageDescriptor | undefined {
    return this.byFilename(path) ?? this.byExtension(path);
  }
}
